import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  selectActiveProjects,
  selectBasemap,
  selectFilteredProjectsInActiveFeature,
  selectActiveFeature,
  selectSidebarIsOpen,
  selectSidebarScrollup,
  selectCases,
  selectSuburbsByPostcode,
  selectRiskByPostcode,
  selectActiveDataset,
  selectLatestDate,
  selectMaxCases,
  selectMaxRisk,
  selectMinRisk,
} from "../../reducers";
import PropTypes from "prop-types";
import {
  setActiveProject,
  setSidebarIsOpen,
  scrollToTopOfSidebar,
  setActiveDataset
} from "../../actions/ui";
import styles from "./sidebar.module.scss";
import InfoCard from "../infoCard";
import Legend from "../legend";
import find from "lodash/find";
import { colors } from "../../modules/theme";
import {
  MapMarkerAlt,
} from "../icons/icons";
import { projectShape } from "../../modules/project";
import { useIsMobile } from "../../hooks/mobile.js";
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import FlareIconOutlined from '@material-ui/icons/FlareOutlined';
import GrainIconOutlined from '@material-ui/icons/GrainOutlined';


const Sidebar = props => {
  // activeProjects is an array of clicked circles. Usually 1, but for online businesses could be several

  const {
    activeProjects,
    setActiveProject,
    filteredProjectsInActiveFeature,
    activeFeature,
    sidebarIsOpen,
    setSidebarIsOpen,
    scrollToTopOfSidebar,
    sideBarScroll,
    geometryConfig,
    cases,
    latestDate,
    suburbsByPostcode,
    riskInfo,
    risk,
    maxCases,
    minRisk,
    maxRisk,
    activeDataset,
    setActiveDataset
  } = props;
  const { idSelector } = geometryConfig;

  //eslint-disable-next-line
  const [_viewingProjectIndex, setViewingProjectIndex] = useState(0);

  // Don't include the active projects in the 'See more' list
  const otherShopsInRegion = filteredProjectsInActiveFeature.filter(shop => {
    return !find(activeProjects, { name: shop.name });
  });

  const scrollContainer = useRef(null);

  useEffect(() => {
    setViewingProjectIndex(0);
  }, [activeProjects]);

  // Scroll to the top of the sidebar when something is selected
  useEffect(() => {
    scrollContainer.current.scrollTop = 0;
  }, [sideBarScroll]);

  // Check for mobile, so we can close sidebar on selection if needed
  const isMobile = useIsMobile();


  const [mode, setModes] = React.useState(() => ['risk']);
  const handleMode = (event, newModes) => {
    if (newModes && newModes.length) {
      setModes(newModes);
    }
  };

  return (
    <div
      className={`${ isMobile ?  styles.sidebarContainerMobile : styles.sidebarContainer}`}
      onClick={() => {
        setSidebarIsOpen(!sidebarIsOpen);
      }}
      id="sidebarContainer"
    >
      <div
        onClick={e => e.stopPropagation()}
        className={`${ isMobile ?  styles.sidebarMobile : styles.sidebar}`}
        ref={scrollContainer}
        style={{
          backgroundColor: colors.darkBlue,
          border: `1px solid ${colors.darkBlue}`,
        }}
      >
        <h1 style={{ marginBottom: '30px', fontSize: '30px' }} className={styles.pageHeading}>Occupational Risk in NSW</h1>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 20
          }}
        >
          <img
            style={{ width: 26, height: 26, marginRight: 10 }}
            alt="Logo"
            src="/logo.png"
          />
          <p style={{ color: 'white', margin: 0 }}>By <a style={{ color: 'white' }} href="https://smallmultiples.com.au">Small Multiples</a></p>
        </div>
        <h3>Map showing</h3>
        <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 20, color: 'white' }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleMode}
            aria-label="text alignment"
            size="small"
          >
            <ToggleButton value="risk" aria-label="risk" onChange={() => { console.log(activeDataset === 'cases') }} checked={activeDataset === 'occupations'} onClick={() => { setActiveDataset('occupations') }}><FlareIconOutlined />&nbsp;&nbsp;Occupational Risk</ToggleButton>
            <ToggleButton value="cases" aria-label="cases" onChange={() => { console.log(activeDataset === 'cases') }} checked={activeDataset === 'cases'} onClick={() => { setActiveDataset('cases') }}><GrainIconOutlined />&nbsp;&nbsp;COVID-19 Cases</ToggleButton>
          </ToggleButtonGroup>
        </div>

        <h3>
          Postcode {activeFeature ? activeFeature.properties.POA_CODE16 : ""}
        </h3>
        <div className={styles.activeProjectsContainer}>
          <InfoCard
            shop={activeFeature}
            key={activeFeature}
            riskAvg={risk[idSelector(activeFeature)]? risk[idSelector(activeFeature)]['avg_risk'] : 0}
            activeCard={true}
            riskInfo={riskInfo[idSelector(activeFeature)] ? riskInfo[idSelector(activeFeature)] : {}}
            suburbs={suburbsByPostcode[idSelector(activeFeature)] ? suburbsByPostcode[idSelector(activeFeature)] : []}
            suburbName={cases[idSelector(activeFeature)] ? cases[idSelector(activeFeature)].lga_name19 : undefined}
            numCases={cases[idSelector(activeFeature)] ? cases[idSelector(activeFeature)].dates.length : undefined}
            latestDate={latestDate}
            maxCases={maxCases}
            minRisk={minRisk}
            maxRisk={maxRisk}
          ></InfoCard>
        </div>

        <h3>
          Methodology
        </h3>
        <div className={styles.legend}>
          <h4>Occupational risk</h4>
          <p>The risk of a worker to get infected by COVID-19 based on the occupation is the equal weight aggregation of <a href="https://www.onetonline.org/find/descriptor/result/4.C.2.c.1.b?a=1">exposure to disease</a> and <a href="https://www.onetonline.org/find/descriptor/result/4.C.2.a.3">physical proximity to others</a>. The occupational risk of a suburb is calculated as average of all occupations.</p>
          <h4>Exposure to disease</h4>
          <p>How often does this job require exposure to disease/infections?</p>
          <Legend
            min={0}
            max={100}
            steps={4}
            labels={['Never', 'Once a year', 'Once a month', 'Once a week', 'Daily']}
          ></Legend>
          <h4>Physical proximity to others</h4>
          <p>Are the tasks performed in close contact to other people?</p>
          <Legend
            min={0}
            max={100}
            steps={4}
            labels={["Don't work near people", 'Not closely', 'Slightly close', 'Moderately close', 'Very close']}
          ></Legend>
          <h4>COVID-19 cases in NSW</h4>
          <p><a href="https://data.nsw.gov.au/nsw-covid-19-data">https://data.nsw.gov.au/nsw-covid-19-data</a></p>
        </div>

      </div>
    </div>
  );
};

const mapStateToProps = s => ({
  activeProjects: selectActiveProjects(s),
  basemap: selectBasemap(s),
  filteredProjectsInActiveFeature: selectFilteredProjectsInActiveFeature(s),
  activeFeature: selectActiveFeature(s),
  sidebarIsOpen: selectSidebarIsOpen(s),
  sideBarScroll: selectSidebarScrollup(s),
  cases: selectCases(s),
  latestDate: selectLatestDate(s),
  suburbsByPostcode: selectSuburbsByPostcode(s),
  riskInfo: selectRiskByPostcode(s),
  activeDataset: selectActiveDataset(s),
  risk: selectRiskByPostcode(s),
  maxRisk: selectMaxRisk(s),
  minRisk: selectMinRisk(s),
  maxCases: selectMaxCases(s),
});
const mapDispatchToProps = {
  setActiveProject,
  setSidebarIsOpen,
  scrollToTopOfSidebar,
  setActiveDataset
};
Sidebar.propTypes = {
  setActiveProject: PropTypes.func.isRequired,
  setSidebarIsOpen: PropTypes.func.isRequired,
  setActiveDataset: PropTypes.func.isRequired,
  scrollToTopOfSidebar: PropTypes.func.isRequired,
  activeProjects: PropTypes.array.isRequired,
  activeFeature: PropTypes.object,
  suburbsByPostcode: PropTypes.object,
  activeDataset: PropTypes.string,
  risk: PropTypes.object,
  maxCases: PropTypes.number.isRequired,
  maxRisk: PropTypes.number.isRequired,
  minRisk: PropTypes.number.isRequired,
  filteredProjectsInActiveFeature: PropTypes.arrayOf(
    PropTypes.shape(projectShape),


  ),
  sidebarIsOpen: PropTypes.bool,
  sideBarScroll: PropTypes.number,
  cases: PropTypes.object,
  riskInfo: PropTypes.object,
  geometryConfig: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);



// WEBPACK FOOTER //
// ./src/components/sidebar/sidebar.js